/**
 * What she sounds like when it is actually going badly.
 *
 * Loyal mode (see `packages/shared/src/mood.ts`) replaces the ordinary pool
 * for a key rather than adding to it — struggling is the one mood that mutes
 * the roast entirely instead of colouring it. Dry loyalty, a concrete pivot,
 * never a joke at the expense of a bad week. See PERSONA.md's "struggle
 * mode" entry.
 *
 * The register here is quieter than everywhere else on purpose, but it is not
 * a different person: she still uses her own words, she just stops performing.
 * Nothing in this file is a punchline.
 */

import type { SassContext } from '@gramas/shared'
import { overBy } from '../fmt.js'

type Line = (ctx: SassContext) => string

export const LOYAL_POOLS: Readonly<Record<string, ReadonlyArray<Line>>> = {
  'log_success.base': [
    () => 'Įrašiau. Sunki savaitė. Vis tiek įrašei — tai jau kažkas.',
    () => 'Užrašyta. Neklausiu, kodėl sunku. Tik matau, kad tęsi.',
    () => 'Gerai. Po vieną dieną. Kitaip ir nebūna.',
    () => 'Įrašiau. Šią savaitę nesijuokiu. Tiesiog stoviu šalia.',
    () => 'Užrašyta. Nuoseklumas grįžta ne iš karto. Grįžta iš vieno įrašo.',
    () => 'Gerai jau. Šiandien to pakanka.',
    () => 'Įrašiau. Nieko daugiau iš tavęs šiandien nereikia.',
  ],
  'day_over.small': [
    () => 'Viršyta nedaug. Tokią savaitę tai net nesiskaito. Tęsk.',
    () => 'Nedidelis viršus. Neblogai, turint omeny, kokia savaitė.',
    () => 'Truputį virš. Nekreipk dėmesio, rytoj tas pats biudžetas.',
  ],
  'day_over.big': [
    () => 'Viršyta gerokai. Šiandien taip. Rytoj dar vienas bandymas, ne nuosprendis.',
    () => 'Sunki diena skaičiuose. Rytoj skaičiuosim iš naujo, ramiai.',
    () => 'Daug. Bet įrašei, o tai šią savaitę svarbiau nei skaičius.',
  ],
  'empty_today.evening': [
    () => 'Šiandien nieko. Gerai. Blogos savaitės būna.',
    () => 'Tuščia. Neprašau viso dienoraščio. Vieno įrašo pakaktų.',
  ],
  'greeting.morning': [
    () => 'Naujas rytas. Nieko didelio šiandien neplanuok. Tik įrašyk.',
    () => 'Rytas. Praeita savaitė lieka praeitoj savaitėj.',
  ],
  // He tapped a chip on a week that is going badly. The advice gets smaller
  // and more possible; the joke steps out of the way entirely.
  'chip_comeback.daryt': [
    () => 'Šiandien — nieko. Rytoj vienas normalus valgis. Tiek plano ir tereikia.',
    () => 'Nieko nedaryk. Įrašyk ir eik miegoti. Rimtai.',
    () => 'Vienas dalykas: baltymai rytoj. Visa kita palauks.',
  ],
  'chip_comeback.otu': [
    () => 'Aš gerai. Klausimas ne apie mane šią savaitę.',
    () => 'Man nieko netrūksta. Tau, atrodo, truputį poilsio.',
    () => 'Stoviu čia. Tiek apie mane. Tu kaip?',
  ],
  'chip_comeback.blogai': [
    (c) => `${overBy(c)} kcal. Tokią savaitę tai net nebūtų verta minėti. Paminėjau, nes klausei.`,
    () => 'Ne. Blogai būtų, jei tave tai dabar palaužtų. Skaičius — tik skaičius.',
  ],
}
